import {
  Alert,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material"
import { Actions, useStoreActions } from "easy-peasy"
import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { CreateCustomerInput, Customer } from "../../graphql/types"
import { AppRoutes } from "../../store/routes"
import { StoreModel } from "../../store/store"

const CreateCustomerPage: React.FC = () => {
  const navigate = useNavigate()
  const createCustomer = useStoreActions(
    (actions: Actions<StoreModel>) => actions.createCustomer
  )
  const [formData, setFormData] = useState<CreateCustomerInput>({
    customerName: "",
    legalName: "",
    customerClass: "INLAND", // Changed default to INLAND
    currencyID: "EUR", // Default or let user choose
    status: "ACTIVE", // Default or let user choose
    terms: null,
    taxZone: null,
    locationName: null,
  })
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | { name?: string; value: unknown }
    >
  ) => {
    const name = event.target.name as keyof CreateCustomerInput
    const value = event.target.value
    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? null : value, // Handle optional fields being cleared
    }))
  }

  // Handle Select change separately because event.target for Select is different
  const handleSelectChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const name = event.target.name as keyof CreateCustomerInput
    const value = event.target.value
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    if (
      !formData.customerName ||
      !formData.legalName ||
      !formData.customerClass ||
      !formData.currencyID ||
      !formData.status
    ) {
      setError(
        "Required fields (Customer Name, Legal Name, Customer Class, Currency, Status) cannot be empty."
      )
      setLoading(false)
      return
    }

    try {
      const newCustomer: Customer | null = await createCustomer(formData)
      if (newCustomer && newCustomer.id) {
        setSuccess(
          `Customer '${newCustomer.customerName}' created successfully!`
        )
        setTimeout(() => {
          // Assuming customerID from the response is the correct identifier for the details page route
          // Adjust if your AppRoutes.CUSTOMER_DETAIL expects a different param (e.g., newCustomer.id)
          navigate(`${AppRoutes.CUSTOMERS}/${newCustomer.customerID}`)
        }, 1500)
      } else {
        setError("Failed to create customer or customer ID not returned.")
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred.")
    }
    setLoading(false)
  }

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom component="div">
          Neuen Kunden erstellen
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Kundenname"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Offizieller Name"
                name="legalName"
                value={formData.legalName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="customerClass-label">Kundenklasse</InputLabel>
                <Select
                  labelId="customerClass-label"
                  id="customerClass"
                  name="customerClass"
                  value={formData.customerClass}
                  label="Kundenklasse"
                  onChange={handleSelectChange as any} // Cast to any to handle MUI Select event type
                >
                  <MenuItem value="INLAND">INLAND</MenuItem>
                  <MenuItem value="EU">EU</MenuItem>
                  <MenuItem value="DRITTLAND">DRITTLAND</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Währungs-ID"
                name="currencyID"
                value={formData.currencyID}
                onChange={handleChange}
                // Consider making this a Select
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                // Consider making this a Select (e.g., ACTIVE, INACTIVE)
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bedingungen (Optional)"
                name="terms"
                value={formData.terms || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Steuerzone (Optional)"
                name="taxZone"
                value={formData.taxZone || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Standortname (Optional)"
                name="locationName"
                value={formData.locationName || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : "Kunde erstellen"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  )
}

export default CreateCustomerPage
